import { ContentSwitcher, SingleSelectDropdown, toast } from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { createLogger } from "@/logging";
import { hatcheryDropdownModes } from "@/app/theme";
import { GnbMenu } from "@/components/base";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import {
  ALL_SESSION_FILTER,
  clearPolicySelectionParam,
  readCatalogFilters,
  readPolicySelectionIds,
  toEvalRunScope,
  writePolicySelectionParam,
  type SelectedPolicyRef,
  type SessionScope,
} from "@/modules/training/public";
import { openEvaluationResultsWindow } from "./evaluationResultsWindow";
import { useLegacyResultsDeepLink } from "./useLegacyResultsDeepLink";
import { useResultsWindowUrlSync } from "./useResultsWindowUrlSync";
import { PolicyComparePanel } from "./compare/PolicyComparePanel";
import { fetchPolicyCompareGroups, type EvalCompareGroup } from "./compare/policyCompareGroups";
import {
  chipsToPolicyTextFilters,
  clearPolicyFilterParams,
  createPolicyTableSource,
  DEFAULT_EVALUATION_PAGE_SIZE,
  readPolicyFilterChips,
  readPolicyRegisteredWithin,
  scopeSearchParamKey,
  TrainingPoliciesPanel,
  useMemberExperimentSelection,
  useTeamGroupSelection,
  writeScopeParam,
  type PolicyQuery,
  type PolicyTableSource,
} from "@/page/training/evaluationPolicyCatalog";

// Evaluation 페이지다. training 이 만든 정책의 카탈로그와 평가 실행 진입점이다. 행을 클릭하거나,
// 선택한 뒤 View Evaluation Results 를 누르면 그 정책 조합의 Evaluation Results 전체화면 창을
// 연다(`EvaluationResultsWindowContent`).
//
// scope 는 Training 페이지와 같이 동작한다. team/me 토글이 정책 카탈로그의 scope 를 고른다.
// team scope 는 그룹 드롭다운으로 더 좁힌다. 두 값 모두 URL 에 보존한다. 토글은 페이지 제목
// 옆(titleAside)에 둔다. 토글은 어떤 카탈로그인지를 나타내고, 좁히는 조건이 아니기 때문이다.
// 그룹 드롭다운은 다른 축과 함께 패널의 필터 행에 둔다.

const logger = createLogger("page/evaluationResults/EvaluationPage");

const scopeOptions = [
  { value: "me", label: "My" },
  { value: "team", label: "Team" },
];

function normalizeScope(value: string | null): SessionScope {
  return value === "team" ? "team" : "me";
}

// 그룹을 바꾸면 이전 그룹의 필터*와* 체크박스 선택을 버린다. 그룹 변경과 같은 URL 커밋에서
// 버린다. 그래서 어떤 history 항목도 한 그룹과 다른 그룹의 선택을 함께 가지지 않는다. 또한
// 선택 미러가 일부만 갱신된 URL 과 경쟁하지 않는다.
function clearGroupScopedParams(params: URLSearchParams): URLSearchParams {
  return clearPolicySelectionParam(clearPolicyFilterParams(params));
}

export function EvaluationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = normalizeScope(searchParams.get(scopeSearchParamKey));
  // 행 수는 scope 를 key 로 쓰는 subtree 위에서 보관한다. 그래서 scope 전환으로 remount 해도
  // 고른 크기가 유지되고, 다시 만든 소스가 그 크기로 시작한다.
  const [policyPageSize, setPolicyPageSize] = useState(DEFAULT_EVALUATION_PAGE_SIZE);

  // 그룹 선택기와 team scope 조회는 team scope 에만 있다. 그룹을 바꾸면 같은 URL 커밋에서
  // 그룹에 속한 member/experiment 필터와 정책 선택도 버린다.
  const group = useTeamGroupSelection(scope === "team", clearGroupScopedParams);

  const catalogFilter = useMemberExperimentSelection(
    scope,
    scope === "team" ? group.selectedGroupUuid : null,
  );
  const { experimentId } = catalogFilter;
  const memberUuid = scope === "team" ? catalogFilter.memberUuid : ALL_SESSION_FILTER;
  // 정책 카탈로그를 좁히는 축이다. 소스가 한 번에 적용하는 형태다. memo 로 만들어서 setQuery
  // effect 가 매 렌더의 새 객체가 아니라 URL 에 따라 실행되게 한다.
  const policyQuery = useMemo(
    () => ({
      memberUuid,
      experimentId,
      textFilters: chipsToPolicyTextFilters(readPolicyFilterChips(searchParams)),
      registeredWithin: readPolicyRegisteredWithin(searchParams),
    }),
    [memberUuid, experimentId, searchParams],
  );

  // scope 를 전환하면 같은 커밋에서 이전 scope 의 member/experiment 필터도 버린다. 그래서 어떤
  // history 항목도 한 scope 와 다른 scope 의 필터를 함께 가지지 않는다.
  const handleScopeChange = useCallback(
    (next: string | null) => setSearchParams((prev) => writeScopeParam(prev, next)),
    [setSearchParams],
  );

  // 페이지 제목 옆에 둔다. size 를 지정하지 않으면 c6 기본값(md)이고, 헤더 동작 버튼과 높이가 같다.
  const scopeSwitcher = (
    <ContentSwitcher
      label="범위"
      value={scope}
      options={scopeOptions}
      onValueChange={handleScopeChange}
    />
  );

  // 그룹으로 좁힐 수 있는 것은 team scope 뿐이다. Training 페이지의 그룹 선택기와 같은 컨트롤
  // (field mode, 같은 문구)이다. 두 페이지는 scope/group 어휘를 공유한다.
  const groupFilter =
    scope === "team" ? (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="그룹"
        value={group.selectedGroupUuid ?? ""}
        options={group.options}
        onValueChange={group.onGroupChange}
        placeholder="선택할 그룹이 없습니다."
        disabled={!group.hasGroups}
      />
    ) : null;

  // 페이지가 좁히는 축을 소유하고 패널에 노드 하나로 전달한다. 그래서 패널이 remount 해도
  // member/experiment 목록이 유지된다. 두 목록 엔드포인트에는 api 계층 캐시가 없다.
  // member 와 experiment 는 Training 세션 필터 바와 같은 facet 드롭다운이다. 검색할 수 있고,
  // 중립 값은 "전체" 다. 그래서 무언가를 고르기 전에는 강조 표시가 없다.
  const queryControls = (
    <>
      {groupFilter}
      {scope === "team" ? (
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          label="멤버"
          neutralValue={ALL_SESSION_FILTER}
          searchable
          value={memberUuid}
          options={catalogFilter.memberOptions}
          onValueChange={catalogFilter.onMemberChange}
        />
      ) : null}
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.filter}
        label="실험"
        neutralValue={ALL_SESSION_FILTER}
        searchable
        value={experimentId}
        options={catalogFilter.experimentOptions}
        onValueChange={catalogFilter.onExperimentChange}
      />
    </>
  );

  // 정책 소스는 생성할 때 scope 와 행 클릭 연결을 고정한다. 그래서 key 기반 remount 로 소스를
  // 소유하는 subtree 를 다시 만든다. scope 전환이 선택을 지우는 곳도 여기다. 다른 scope 는
  // 다른 카탈로그이기 때문이다.
  return (
    <EvaluationScopedContent
      key={scope}
      scope={scope}
      groupUuid={scope === "team" ? group.selectedGroupUuid : null}
      groupsResolving={group.isResolvingGroups}
      policyQuery={policyQuery}
      policyPageSize={policyPageSize}
      onPolicyPageSizeChange={setPolicyPageSize}
      scopeSwitcher={scopeSwitcher}
      queryControls={queryControls}
    />
  );
}

// 정책 소스를 소유하는 subtree 이고, scope 를 key 로 쓴다. 정책 패널이 아니라 페이지가 이
// subtree 를 소유한다. 그래서 패널이 remount 해도 체크박스 선택이 유지된다.
function EvaluationScopedContent({
  scope,
  groupUuid,
  groupsResolving,
  policyQuery,
  policyPageSize,
  onPolicyPageSizeChange,
  scopeSwitcher,
  queryControls,
}: {
  scope: SessionScope;
  groupUuid: string | null;
  groupsResolving: boolean;
  policyQuery: Omit<PolicyQuery, "groupUuid">;
  policyPageSize: number;
  onPolicyPageSizeChange: (pageSize: number) => void;
  scopeSwitcher: ReactNode;
  queryControls: ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // 행 클릭 연결은 소스를 생성할 때 고정된다. 그러나 창은 최신 scope·필터 스냅샷으로 열어야
  // 한다. 그래서 ref 를 거쳐 호출한다.
  const openResultsWindowRef = useRef<(policies: readonly SelectedPolicyRef[]) => void>(() => {});
  // key 기반 마운트마다 인스턴스를 하나 만든다. initializer 는 첫 렌더의 URL 필터와 ?policy= 선택
  // seed 를 가져온다. seed 는 카탈로그를 받은 뒤 복원한다. 행을 클릭하면 그 정책을 선택에 추가하고,
  // 그 정책 하나의 Evaluation Results 창을 연다. 그래서 창과 도구 띠·?policy= 미러가 같은 정책을
  // 가리킨다. 이미 선택된 행은 그대로 두고, 선택 상한에 막히면 창만 연다. 열람은 read-only 라
  // canEvaluate 와 관계없다.
  const [policySource] = useState(() => {
    const source = createPolicyTableSource({
      scope,
      onRowActivate: (row) => {
        source.setRowSelected(row, true);
        openResultsWindowRef.current([{ policyId: row.getRowId(), policyName: row.name }]);
      },
      ...policyQuery,
      selectedPolicyIds: readPolicySelectionIds(searchParams),
      pageSize: policyPageSize,
    });
    return source;
  });
  useEffect(() => {
    void policySource.initialize();
    return () => policySource.dispose();
  }, [policySource]);

  // URL(+ 해석한 그룹)을 소스에 전달한다. 정책 패널이 아니라 여기에 둔다. 그래서 그룹 변경이
  // 항상 소스에 전달되고, 이전 그룹의 선택을 버린다.
  useEffect(() => {
    void policySource.setQuery({ groupUuid, ...policyQuery });
  }, [policySource, groupUuid, policyQuery]);
  useEffect(() => {
    policySource.setGroupResolving(groupsResolving);
  }, [policySource, groupsResolving]);

  // 행 수를 소스에 전달한다. 마운트 때는 아무것도 하지 않는다. 생성자가 이미 이 크기를 가진다.
  useEffect(() => {
    void policySource.setPageSize(policyPageSize);
  }, [policySource, policyPageSize]);

  // 선택 멤버십만 구독한다. 로드와 페이지 이동 이벤트는 같은 selectedIds 참조를 다시 쓴다.
  // 그래서 여기서 중단되고, 페이지와 활성 패널 subtree 를 다시 렌더하지 않는다.
  const selectedIds = useSyncExternalStore(
    policySource.subscribe,
    () => policySource.getSnapshot().selectedIds,
  );
  const selectionHydrated = useSyncExternalStore(policySource.subscribe, () =>
    policySource.isSelectionHydrated(),
  );

  // 선택을 ?policy= 미러에 쓴다. 의도적으로 setQuery effect 뒤에 선언한다. 그룹 변경 커밋은
  // 그 effect 에서 먼저 선택을 지운다. 그래서 이 미러는 방금 지운 param 위에 오래된 선택을 쓰지 않는다.
  useEffect(() => {
    // URL seed 를 카탈로그로 해석하기 전에는, 아직 빈 선택이 복원에 쓸 param 을 지우면 안 된다.
    if (!selectionHydrated) {
      return;
    }
    const liveIds = policySource.getSnapshot().selectedIds;
    // 같은 flush 의 앞선 effect 가 선택을 이미 바꿨을 수 있다(그룹 초기화). 그 이벤트가 다시
    // 렌더를 예약했다. 그래서 오래된 id 를 쓰지 않고 그 렌더에 맡긴다.
    if (liveIds !== selectedIds) {
      return;
    }
    const ids = [...liveIds];
    const urlIds = readPolicySelectionIds(searchParams);
    if (ids.length === urlIds.length && urlIds.every((id) => liveIds.has(id))) {
      return;
    }
    setSearchParams((prev) => writePolicySelectionParam(prev, ids), { replace: true });
  }, [policySource, selectionHydrated, selectedIds, searchParams, setSearchParams]);
  // 선택된 행 객체는 소스에 있다. 스냅샷의 id 집합이 변경 신호다. filter 는 결과를 렌더한
  // 집합과 정확히 같게 만든다.
  const selectedPolicies = useMemo(
    () => policySource.getSelectedPolicies().filter((policy) => selectedIds.has(policy.policyId)),
    [policySource, selectedIds],
  );

  // Evaluation Results 창을 연다. scope·카탈로그 축은 열 때의 스냅샷으로 창 params 에 담긴다.
  // 이후 페이지에서 축을 바꿔도 열린 창은 바뀌지 않는다.
  const openResultsWindow = useCallback(
    (policies: readonly SelectedPolicyRef[]) => {
      // 그룹을 해석하지 않은 team 스코프는 카탈로그가 비어 행도 선택도 없다. 이 코드는 타입 가드다.
      const runScope = toEvalRunScope(scope, groupUuid);
      if (runScope === null) {
        return;
      }
      openEvaluationResultsWindow({
        scope: runScope,
        policies,
        catalogFilters: readCatalogFilters(searchParams, scope),
      });
    },
    [scope, groupUuid, searchParams],
  );
  openResultsWindowRef.current = openResultsWindow;

  const viewEvaluationResults = useCallback(
    () => openResultsWindow(selectedPolicies),
    [openResultsWindow, selectedPolicies],
  );

  // 이전 딥링크(?tab=results, view=, eval_run=, 결과 칩 키)는 창을 자동으로 열고 URL 을 정리한다.
  useLegacyResultsDeepLink({ scope, groupUuid, groupsResolving, policySource, selectionHydrated });

  // 활성 결과 창의 정책 조합과 ?results_policy 를 서로 맞춘다. 행 클릭이나 선택으로 연 창이 URL 에
  // 남는다. 그래서 새로고침하거나 공유하면 같은 조합의 창을 복원한다.
  useResultsWindowUrlSync({ scope, groupUuid, groupsResolving, policySource });

  // 정책 비교다. 선택한 정책마다 최근 succeeded run 을 병렬로 받아 우측 비교 패널을 연다.
  // 조회가 실패하면 열지 않는다. 일부 정책이 경고 없이 빠진 비교는 오해를 만든다. 팝업 상태는
  // 이 scoped subtree 가 소유한다. 그래서 스코프 전환이나 페이지 이탈 때 상태를 함께 버린다.
  // SessionComparePanel 과 같은 로컬 상태 계약이다.
  //
  // 세대 가드: 새 요청과 unmount 는 세대 값을 늘린다. 이전 세대의 응답은 버린다. 그래서 다시
  // 클릭하거나 unmount 한 뒤에 도착한 응답이 버린 선택의 비교를 열지 않는다.
  const [policyCompare, setPolicyCompare] = useState<readonly EvalCompareGroup[] | null>(null);
  const compareGenerationRef = useRef(0);
  useEffect(
    () => () => {
      compareGenerationRef.current += 1;
    },
    [],
  );
  const compareSelectedPolicies = useCallback(async () => {
    // 그룹을 해석하지 않은 team 스코프는 카탈로그가 비어 2개 이상 선택할 수 없다. 이 코드는 타입 가드다.
    const runScope = toEvalRunScope(scope, groupUuid);
    if (runScope === null) {
      return;
    }
    const policies = policySource.getSelectedPolicies();
    if (policies.length < 2) {
      return;
    }
    const generation = ++compareGenerationRef.current;
    try {
      const groups = await fetchPolicyCompareGroups(policies, runScope);
      if (generation !== compareGenerationRef.current) {
        return;
      }
      setPolicyCompare(groups);
    } catch (error: unknown) {
      logger.error("Failed to load evaluation runs for the policy comparison.", error);
      if (generation !== compareGenerationRef.current) {
        return;
      }
      toast.error("선택한 Policy 의 Evaluation 실행을 불러오지 못했습니다. 다시 시도해 주세요.");
    }
  }, [scope, groupUuid, policySource]);

  return (
    <>
      <EvaluationPageView
        pageTitle="Evaluation"
        pageTitleTooltip="Simulation 으로 분류된 Dataset 으로 학습한 Policy 만 보여줍니다."
        currentMenu={GnbMenu.TrainingEvaluation}
        // 모든 evaluation 딥링크가 이 페이지로 오므로 진입 게이트는 여기 하나다.
        requiredPermission="can_use_evaluation"
        titleAside={scopeSwitcher}
        scope={scope}
        onPolicyPageSizeChange={onPolicyPageSizeChange}
        queryControls={queryControls}
        policySource={policySource}
        onViewEvaluationResults={viewEvaluationResults}
        onCompareSelectedPolicies={compareSelectedPolicies}
      />
      {policyCompare !== null ? (
        <PolicyComparePanel groups={policyCompare} onClose={() => setPolicyCompare(null)} />
      ) : null}
    </>
  );
}

type EvaluationPageViewProps = PageBaseProps & {
  scope: SessionScope;
  // 패널은 사용자가 고른 페이지당 행 수를 페이지에 알린다. 이 설정은 페이지가 소유한다.
  // 크기는 주입한 소스를 거쳐 표에 전달된다.
  onPolicyPageSizeChange: (pageSize: number) => void;
  // 그룹 선택기와 Team member / experiment 축이다. 노드 하나로 받으므로 축을 추가해도
  // signature 가 바뀌지 않는다. 이 노드를 감싸는 행은 패널이 소유한다. scope 전환기는 titleAside 다.
  queryControls: ReactNode;
  // 페이지가 소유하는 정책 소스다. 패널이 remount 해도 선택이 유지된다.
  policySource: PolicyTableSource;
  onViewEvaluationResults: () => void;
  onCompareSelectedPolicies: () => Promise<void>;
};

class EvaluationPageView extends PageBase<EvaluationPageViewProps> {
  protected override renderContent(_context: PageBaseContext): ReactNode {
    const {
      scope,
      onPolicyPageSizeChange,
      queryControls,
      policySource,
      onViewEvaluationResults,
      onCompareSelectedPolicies,
    } = this.props;

    // key 가 필요 없다. scope 가 바뀌면 위의 scope-key subtree 가 이미 이 패널을 remount 하고
    // 주입한 소스를 다시 만든다.
    return (
      <TrainingPoliciesPanel
        scope={scope}
        onPageSizeChange={onPolicyPageSizeChange}
        queryControls={queryControls}
        source={policySource}
        onViewEvaluationResults={onViewEvaluationResults}
        onCompareSelectedPolicies={onCompareSelectedPolicies}
      />
    );
  }
}
